<template>
  <div class="auth-stage" :data-focus="focused ? 'on' : null">
    <AuthBackground ref="background" :motion="motion" />

    <section class="form-col">
      <div class="form-card" :class="{ 'form-card--wide': wide }">
        <slot name="brand">
          <AuthBrandLogo mode="plain" class="brand-logo--card" />
          <div class="brand-mark"><brand-wordmark /></div>
        </slot>
        <slot />
      </div>
    </section>

    <!-- nama kelasnya dipakai intro buat ngecualiin klik skip -->
    <div class="auth-controls">
      <slot name="controls" />
      <button type="button" class="stage-btn" @click="toggleTheme">{{ themeLabel }}</button>
      <button
        v-if="intro && !reduceMotion"
        type="button"
        class="stage-btn"
        :aria-pressed="soundEnabled ? 'true' : 'false'"
        @click="toggleSound"
      >{{ soundLabel }}</button>
    </div>

    <AuthIntroSequence
      v-if="sequence"
      :busy="busy"
      :intro="intro"
      :sound="soundEnabled"
      @done="onSequenceDone"
    />
  </div>
</template>

<script>
import AuthBackground from '@/components/auth/AuthBackground';
import AuthBrandLogo from '@/components/auth/AuthBrandLogo';
import AuthIntroSequence from '@/components/auth/AuthIntroSequence';
import BrandWordmark from '@/components/auth/BrandWordmark';
import { applyTheme, oppositeTheme, readStoredTheme } from '@/util/auth/authTheme';
import { readSoundPref, writeSoundPref } from '@/util/auth/introSound';

const FOCUS_MOTION = 0.34;
const BLUR_DELAY = 120;
const FIELD_TAGS = ['INPUT', 'SELECT', 'TEXTAREA'];

export default {
  name: 'AuthStage',
  components: {
    AuthBackground, AuthBrandLogo, AuthIntroSequence, BrandWordmark,
  },
  props: {
    busy: {
      type: Boolean,
      default: false,
    },
    intro: {
      type: Boolean,
      default: false,
    },
    // veil loading + intro; halaman sesudah login gak butuh
    sequence: {
      type: Boolean,
      default: true,
    },
    wide: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return {
      theme: 'light',
      focused: false,
      reduceMotion,
      soundEnabled: reduceMotion ? false : readSoundPref(),
    };
  },
  computed: {
    // satu keadaan fokus nyetir dua hal: laju latar dan peredupannya lewat data-focus
    motion() { return this.focused ? FOCUS_MOTION : 1; },
    themeLabel() { return this.theme === 'dark' ? 'Light theme' : 'Dark theme'; },
    soundLabel() { return this.soundEnabled ? 'Sound on' : 'Sound off'; },
  },
  created() {
    this.blurTimer = 0;
  },
  mounted() {
    this.theme = applyTheme(readStoredTheme());
    this.syncBackgroundPalette();
    // tanpa veil gak ada yang melepas kelas bootnya
    if (!this.sequence) this.clearBootClass();
    this.$el.addEventListener('focusin', this.onFocusIn);
    this.$el.addEventListener('focusout', this.onFocusOut);
  },
  beforeDestroy() {
    clearTimeout(this.blurTimer);
    this.$el.removeEventListener('focusin', this.onFocusIn);
    this.$el.removeEventListener('focusout', this.onFocusOut);
    // atribut temanya sengaja ditinggal, cuma kelas bootnya yang dilepas
    this.clearBootClass();
  },
  methods: {
    pulse(tone) {
      const { background } = this.$refs;
      if (background) background.pulse(tone);
    },

    syncBackgroundPalette() {
      const { background } = this.$refs;
      if (background) background.syncPalette();
    },

    toggleTheme() {
      this.theme = applyTheme(oppositeTheme(this.theme));
      this.syncBackgroundPalette();
    },

    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      writeSoundPref(this.soundEnabled);
    },

    // begitu kedua veil lepas, latar bootnya udah gak dibutuhin
    onSequenceDone() {
      this.clearBootClass();
    },

    clearBootClass() {
      document.documentElement.classList.remove('is-auth-boot');
    },

    isFormField(node) {
      return !!node && FIELD_TAGS.indexOf(node.tagName) !== -1;
    },

    onFocusIn(event) {
      if (!this.isFormField(event.target)) return;
      clearTimeout(this.blurTimer);
      this.focused = true;
    },

    onFocusOut() {
      // dijeda sesaat biar pindah antar field gak bikin latarnya kedip
      clearTimeout(this.blurTimer);
      this.blurTimer = setTimeout(() => {
        const active = document.activeElement;
        if (!active || !this.$el.contains(active) || !this.isFormField(active)) {
          this.focused = false;
        }
      }, BLUR_DELAY);
    },
  },
};
</script>

<style src="@/assets/css/auth-stage.css"></style>
